import React, { useState } from 'react';
import {
  IAvailableWorkflowActions,
  ISubmissionFile
} from '@publishing-engine/domain/models';
import { FileDisplay } from './FileDisplay';
import { FileEmptyState } from './FileEmptyState';
import { Flex } from '@chakra-ui/react';
import FileUploadModal, { IFileValue } from './FileUploadModal';
import { FormFieldFileUpload } from '@publishing-engine/domain/types/article';
import { FieldHeader } from '../common/FieldHeader';
import { FileDiffModal } from './FileDiffModal';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

export const getFileFieldConfig = ({
  name,
  fieldKey,
  validations
}: {
  name: string;
  fieldKey: string;
  validations: FormFieldFileUpload['params']['validations'];
}) => ({
  name,
  params: {
    validations,
    labels: {
      main: name,
      subtitle: ''
    }
  },
  caseDataFields: {
    main: fieldKey
  }
});

export type FormFieldFile = Omit<FormFieldFileUpload, 'component'> & {
  component: string;
};

type IProps = {
  formFieldDefinition: FormFieldFile;
  value?: ISubmissionFile;
  previousValue?: ISubmissionFile;
  isEditable?: boolean;
  referenceName?: string;
  onChange: (value: IFileValue) => void;
  submissionContextId: string;
  showSubtitle: boolean;
  availableActions: IAvailableWorkflowActions;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
};

export const File: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  isEditable,
  submissionContextId,
  onChange,
  referenceName,
  showSubtitle,
  availableActions,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated, fieldsResponse } =
    useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);
  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);

  return (
    <Flex flexDirection={'column'} gap={2} id={referenceName}>
      {isEditing && (
        <FileUploadModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          value={value}
          onClose={() => {
            setIsEditing(false);
            onchange;
          }}
          onSubmit={value => {
            setIsEditing(false);
            // @ts-ignore TODO: fix types
            onChange(value.fileSet[formFieldDefinition.name]);
          }}
          submissionContextId={submissionContextId}
          referenceName={referenceName}
          submissionFields={fieldsResponse?.fields}
        />
      )}

      {isUpdated && showDiffModal ? (
        <FileDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
          submissionContextId={submissionContextId}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        subtitle={
          showSubtitle ? formFieldDefinition.params.labels.subtitle : ''
        }
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {value ? (
        <FileDisplay file={value} submissionContextId={submissionContextId} />
      ) : (
        <FileEmptyState fieldLabels={formFieldDefinition.params.labels} />
      )}
    </Flex>
  );
};
